import { useEffect, useState } from "react";
import { Loader2, Upload, FileCheck2, X, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useDropdowns } from "@/hooks/useMeta";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function WonDialog({ open, onOpenChange, title = "Order Won — congratulations!", confirmLabel = "Save & Mark Won", saving = false, onConfirm }) {
  const { data: dd } = useDropdowns();
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [po, setPo] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setComments(""); setPo(null); }
  }, [open]);

  const uploadPo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/opportunities/upload-po", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPo({ path: data.storage_path, name: data.filename || file.name });
      toast.success("Purchase Order attached");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const confirm = () => {
    if (!reason) { toast.error("Select the won reason"); return; }
    if (!po) { toast.error("Upload the Purchase Order to mark this order Won"); return; }
    onConfirm({ won_reason: reason, won_comments: comments, po_file: po.path, po_filename: po.name });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="won-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> {title}</DialogTitle>
          <DialogDescription>Select why we won and upload the client's Purchase Order (required).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Won Reason <span className="text-red-500">*</span></Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-9" data-testid="won-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>{(dd?.won_reason || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Purchase Order <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">(PDF / JPG / PNG)</span></Label>
            {po ? (
              <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="won-po-attached">
                <FileCheck2 className="h-4 w-4 shrink-0" />
                <span className="truncate flex-1">{po.name}</span>
                <button onClick={() => setPo(null)} className="text-emerald-600 hover:text-red-500" data-testid="won-po-remove"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500 hover:border-brand hover:text-brand cursor-pointer" data-testid="won-po-upload">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Uploading…" : "Upload Purchase Order"}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { uploadPo(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Comments</Label>
            <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={2} placeholder="PO number, terms, anything notable…" data-testid="won-comments" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="won-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={saving || uploading} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="won-confirm">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
